import { canonicalizeRoutes } from "@/api/routes/routes";
import type { CanonicalizedRoute } from "@/api/models/canonicalizedRoute";
import type { StoredRoute } from "@/api/models/storedRoute";
import type { Favorite } from "@/hooks/useFavorites";

/** One Favorite whose stored routeId moved to a new Canonical Route Id. */
export interface FavoriteRouteChange {
  fromRouteId: string;
  toRouteId: string;
  /** True when the rewritten Favorite collided with another and was merged. */
  merged: boolean;
}

export interface CanonicalRoutesResult {
  favorites: Favorite[];
  changes: FavoriteRouteChange[];
  /** Favorites whose line no longer runs (`canonicalRouteId: null`). Never persisted. */
  discontinuedIds: string[];
}

const routeKey = (agency: string, routeId: string) => `${agency}\u0000${routeId}`;

/** The backend accepts at most this many routes per `/api/routes/canonical` call. */
const MAX_ROUTES_PER_REQUEST = 50;

/**
 * Resolves every distinct agency + routeId pair the Favorites use, batching to
 * the request limit. Any failed batch fails the whole call, so the stored ids
 * are kept and retried on the next load.
 */
export async function fetchCanonicalRoutes(
  favorites: Favorite[],
): Promise<CanonicalizedRoute[]> {
  const pairs = new Map<string, StoredRoute>();
  for (const { agency, routeId } of favorites) {
    pairs.set(routeKey(agency, routeId), { agency, routeId });
  }
  const routes = [...pairs.values()];
  const batches: StoredRoute[][] = [];
  for (let i = 0; i < routes.length; i += MAX_ROUTES_PER_REQUEST) {
    batches.push(routes.slice(i, i + MAX_ROUTES_PER_REQUEST));
  }
  const responses = await Promise.all(
    batches.map((batch) => canonicalizeRoutes({ routes: batch })),
  );
  return responses.flatMap((r) => r.routes);
}

/**
 * Applies a `/api/routes/canonical` response to the stored Favorites
 * (docs/adr/0008).
 */
export function applyCanonicalRoutes(
  favorites: Favorite[],
  resolutions: CanonicalizedRoute[],
): CanonicalRoutesResult {
  const canonical = new Map(
    resolutions.map((r) => [routeKey(r.agency, r.routeId), r.canonicalRouteId]),
  );

  const moved: { from: string; favorite: Favorite }[] = [];
  const discontinuedIds: string[] = [];
  const rewritten = favorites.map((f) => {
    const toRouteId = canonical.get(routeKey(f.agency, f.routeId));
    if (toRouteId === null) discontinuedIds.push(f.id);
    if (!toRouteId || toRouteId === f.routeId) return f;
    const favorite = { ...f, routeId: toRouteId };
    moved.push({ from: f.routeId, favorite });
    return favorite;
  });

  // Rewritten ids can collide with each other or with an already-current
  // Favorite of the same leg. The oldest survives; the rest are dropped.
  const legs = new Map<string, Favorite[]>();
  for (const f of rewritten) {
    const leg = `${f.stopId}\u0000${f.routeId}\u0000${String(f.directionId)}`;
    legs.set(leg, [...(legs.get(leg) ?? []), f]);
  }
  const survivors = new Set<Favorite>();
  const collided = new Set<Favorite>();
  for (const group of legs.values()) {
    survivors.add(group.reduce((a, b) => (b.savedAt < a.savedAt ? b : a)));
    if (group.length > 1) group.forEach((f) => collided.add(f));
  }

  return {
    favorites: rewritten.filter((f) => survivors.has(f)),
    changes: moved.map(({ from, favorite }) => ({
      fromRouteId: from,
      toRouteId: favorite.routeId,
      merged: collided.has(favorite),
    })),
    discontinuedIds,
  };
}
